import {
  Grid,
  CardProps,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Paper,
  Stack,
  Collapse,
  Box,
  PaperProps,
  Button,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ControlPointSharpIcon from '@mui/icons-material/ControlPointSharp';
import GetBreakPointName from '../../../theme/getBreakpoint';
import {
  getUrlConvertedString,
  getSubjectColorNew,
  getSubjectIconName,
  isMobileScreen,
  checkSubjectCodeHide,
} from '../../../helper';
import { ICurriculumForHome } from '../../../store/CurriculumSlice';

const paperStyles: PaperProps['sx'] = {
  padding: '20px',
  marginTop: '15px',
  marginBottom: '15px',
  borderRadius: '20px',
  boxShadow: '0px 14px 40px 0px #6E9DB22E',
  maxWidth: '800px',
  mx: 'auto',
};

interface CurriculumsProps {
  curriculumsForHomePage: Array<ICurriculumForHome>;
  selectedCurriculum: ICurriculumForHome | undefined;
}

const getRowItemCount = (breakPoint: string): number => {
  switch (breakPoint) {
    case 'xs':
      return 1;
    case 'sm':
      return 2;
    case 'md':
      return 3;
    case 'lg':
      return 4;
    default:
      return 4;
  }
};

const CurriculumSection: React.FC<CurriculumsProps> = ({
  curriculumsForHomePage,
  selectedCurriculum,
}) => {
  const params = useParams();
  const isMobile = isMobileScreen();

  const [selectedCurriculumIndex, setSelectedCurriculumIndex] = useState<
    number | undefined
  >(undefined);
  const [rowItemCount, setRowItemCount] = useState<number>(4);
  const [curriculumsList, setCurriculumsList] = useState<
    Array<ICurriculumForHome>
  >([]);
  const [currentCurriculum, setCurrentCurriculum] = useState<
    ICurriculumForHome | undefined
  >(selectedCurriculum);
  const [subjectsVisible, setSubjectsVisible] = useState(true);

  const breakPoint = GetBreakPointName();

  const isSubjectCodeHide = checkSubjectCodeHide(currentCurriculum?.name || '');

  useEffect(() => {
    if (breakPoint) {
      setRowItemCount(getRowItemCount(breakPoint));
    }
  }, [breakPoint]);

  useEffect(() => {
    if (curriculumsForHomePage.length) {
      if (!currentCurriculum && params.curriculum) {
        const urlSelectedCurriculum = curriculumsForHomePage.find(
          (c) => getUrlConvertedString(c.name) === params.curriculum
        );
        setCurrentCurriculum(urlSelectedCurriculum);
      }

      const index = curriculumsForHomePage.findIndex(
        (c) =>
          getUrlConvertedString(c.name) ===
          getUrlConvertedString(currentCurriculum?.name || '')
      );

      const limit = (Math.floor(index / rowItemCount) + 1) * rowItemCount;
      setSelectedCurriculumIndex(limit);

      if (isMobile && index < 4) {
        setCurriculumsList(curriculumsForHomePage.slice(0, 4));
      } else {
        setCurriculumsList(curriculumsForHomePage);
      }
    }
  }, [curriculumsForHomePage, currentCurriculum]);

  const RENDER_CURRICULUMS = (start: number, end: number) => {
    const noRow = Math.round(end / rowItemCount);
    const rows: JSX.Element[] = [];

    for (let i = 0; i < noRow; i += 1) {
      const sliceStart = start + i * rowItemCount;
      const sliceEnd = sliceStart + rowItemCount;
      rows.push(
        <Grid
          key={i}
          container
          justifyContent="center"
          spacing={0.5}
          paddingTop="16px"
        >
          {curriculumsList.slice(sliceStart, sliceEnd).map((c, index) => {
            const isSelected = currentCurriculum?.name === c.name;

            const curriculumCardStyles: CardProps['sx'] = {
              border: `1px solid #1E293B`,
              borderRadius: '38px',
              textAlign: 'center',
              color: '#1E293B',
              backgroundColor:
                isSelected && subjectsVisible ? '#E5F7F6' : undefined,
            };

            return (
              <Grid key={index} item>
                <Card elevation={0} sx={curriculumCardStyles}>
                  <CardActionArea
                    sx={{ display: 'flex' }}
                    onClick={() => {
                      if (currentCurriculum?.name === c.name) {
                        setSubjectsVisible((prev) => !prev);
                      } else {
                        setCurrentCurriculum(c);
                        setSubjectsVisible(true);
                      }
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography component="h6" fontSize="15px">
                          {c.name}
                        </Typography>
                        <ControlPointSharpIcon
                          sx={{
                            transform:
                              isSelected && subjectsVisible
                                ? 'rotate(45deg)'
                                : 'rotate(0deg)',
                            transition: 'transform 0.3s ease-in-out',
                          }}
                        />
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      );
    }

    return rows;
  };

  return (
    <Box component="section" margin="10px 0px">
      {RENDER_CURRICULUMS(0, selectedCurriculumIndex || 0)}

      {/* Subject List */}
      {currentCurriculum?.subjects?.length ? (
        <Collapse in={subjectsVisible}>
          <Paper sx={paperStyles}>
            <Grid container spacing={2}>
              {currentCurriculum.subjects.map((subjectItem, index) => {
                const subjectIndex = index + 1;

                const curriculum = getUrlConvertedString(
                  currentCurriculum.name
                );
                const subject = getUrlConvertedString(subjectItem.name);
                const subjectLevel = getUrlConvertedString(
                  subjectItem.subjectLevel
                );
                const subjectCode = getUrlConvertedString(
                  subjectItem.subjectCode
                );

                return (
                  <Grid key={subjectIndex} item xs={12} sm={6} md={4}>
                    <Card
                      elevation={0}
                      sx={{
                        backgroundColor: `${getSubjectColorNew(
                          subjectItem.name
                        )}1A`,
                        borderRadius: '7px',
                        '&:hover': {
                          backgroundColor: `${getSubjectColorNew(
                            subjectItem.name
                          )}4A`,
                        },
                      }}
                    >
                      <CardActionArea
                        component={Link}
                        to={`${process.env.PUBLIC_URL}/dashboard/${curriculum}/${subject}/${subjectLevel}/${subjectCode}`}
                      >
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between">
                            <Box display="flex" alignItems="center" gap={2}>
                              <img
                                src={`${
                                  process.env.PUBLIC_URL
                                }/images/landingPage/newLandingPage/icons/icon-${getSubjectIconName(
                                  subjectItem.name
                                )}.png`}
                                alt="subject-img"
                                style={{ height: '18px' }}
                              />
                              <Box>
                                <Typography
                                  variant="subtitle1"
                                  className="ellipsis-text"
                                >
                                  {subjectItem.name}
                                </Typography>
                                <Typography variant="body2" fontSize="12px">
                                  {isSubjectCodeHide
                                    ? subjectItem.grade
                                    : subjectItem.subjectCode}{' '}
                                  ({subjectItem.subjectLevel})
                                </Typography>
                              </Box>
                            </Box>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Collapse>
      ) : null}

      {RENDER_CURRICULUMS(selectedCurriculumIndex || 0, curriculumsList.length)}

      {isMobile && curriculumsForHomePage.length > curriculumsList.length && (
        <Stack alignItems="center">
          <Button
            color="secondary"
            onClick={() => setCurriculumsList(curriculumsForHomePage)}
            sx={{ marginTop: '20px' }}
          >
            View more
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default CurriculumSection;
